/**
 * @typedef {Object} LicenseOwner
 * @property {String} name - The full name of the license owner.
 * @property {String} email - The email address of the license owner.
 */

/**
 * @typedef {Object} Addons
 * @property {Number} available - The number of addons available to download for the license owner.
 * @property {Number} total - The total number of addons available under the highest plan.
 */

/**
 * @typedef {Object} CTAButton
 * @property {String} label - The label of the button.
 * @property {String} url - The URL linked by the button.
 * @property {String} [icon] - (Optional) An icon to display alongside the button label.
 */

/**
 * @typedef {Object} LicenseStatusMessage
 * @property {('success'|'error')} type - The type of message, e.g., "success" or "error".
 * @property {String} heading - The heading for the license message.
 * @property {String} [content] - (Optional) Additional details about the license status.
 */

/**
 * @typedef {Object} LicenseInformation
 * @property {String} scenario_id - The scenario ID, like `pro-not-installed`, `pro-installed-but-deactivated`, etc.
 * @property {LicenseStatusMessage} notice - The notice to display.
 * @property {LicenseOwner} license_owner - Information about the license owner.
 * @property {("lite"|"basic"|"plus"|"elite")} current_plan - The current plan of the license.
 * @property {Addons} addons - Information about available addons.
 * @property {("active"|"inactive"|"expired")} status - The status of the license.
 * @property {CTAButton[]} cta - A list of call-to-action buttons to manage the license.
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Web component representing a box with a license information
 *
 * @augments LitElement
 */
@customElement( 'uo-license-summary' ) // eslint-disable-line require-jsdoc
export class AutomatorLicenseSummary extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * License information for a user.
	 *
	 * @type {LicenseInformation}
	 */
	@state( { type: Object } )
	accessor _licenseDetails = UncannyAutomatorBackend._site.automator.license_details;

	/**
	 * Returns the data filtered by the filterData function
	 *
	 * @return {LicenseInformation} The data filtered by the filterData function
	 */
	get data() {
		// Check if there is a filter
		if ( isDefined( this.filterData ) ) {
			return this.filterData( this._licenseDetails );
		}

		return this._licenseDetails;
	}

	/**
	 * Renders the component
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	render() {
		return html`
			<uo-alert 
				class="license"
				type=${ this.data.notice?.type }
				heading=${ this.data.notice?.heading }
			>
				${ this._templateNoticeContent }
			</uo-alert>
		`;
	}

	/**
	 * Returns the template that displays the notice content
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateNoticeContent() {
		return html`
			<div class="license-notice">
				${ this._templateSummary }
				${ this._templateTextContent }
				${ this._templateCTAS }
			</div>
		`;
	}

	/**
	 * Returns the template that displays the text content
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateTextContent() {
		// If there is no content, don't render anything
		if ( ! isDefined( this.data.notice?.content ) ) {
			return;
		}

		return html`
			<div class="license-text-content">
				<uo-markdown
					allowed-tags="strong,em"
					.content=${ this.data.notice?.content }
				></uo-markdown>
			</div>
		`;
	}

	/**
	 * Returns the template that displays the summary
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateSummary() {
		return html`
			<div class="license-summary">
				${ this._templateSummaryAccount }
				${ this._templateSummaryCurrentPlan }
				${ this._templateSummaryAddons }
			</div>
		`;
	}

	/**
	 * Returns the template that displays the account information
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateSummaryAccount() {
		return html`
			<div class="license-summary-item">
				<uo-icon id="user"></uo-icon>
				<span class="license-summary-item-label">
					${ escapeHTML( _x( 'Account:', 'License', 'uncanny-automator' ) ) }
				</span>
				<span class="license-summary-item-value">
					${ this.data.license_owner.name } (${ this.data.license_owner.email })
				</span>
			</div>
		`;
	}

	/**
	 * Returns the template that displays the current plan
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateSummaryCurrentPlan() {
		return html`
			<div class="license-summary-item">
				<uo-icon id="gem"></uo-icon>
				<span class="license-summary-item-label">
					${ escapeHTML( _x( 'Plan:', 'License', 'uncanny-automator' ) ) }
				</span>
				<span class="license-summary-item-value">
					${ {
						lite: escapeHTML( _x( 'Lite', 'License', 'uncanny-automator' ) ),
						basic: html`
							<uo-pro-tag></uo-pro-tag> 
							${ escapeHTML( _x( 'Basic', 'License', 'uncanny-automator' ) ) }
						`,
						plus: html`
							<uo-pro-tag></uo-pro-tag> 
							${ escapeHTML( _x( 'Plus', 'License', 'uncanny-automator' ) ) }
						`,
						elite: html`
							<uo-pro-tag></uo-pro-tag> 
							${ escapeHTML( _x( 'Elite', 'License', 'uncanny-automator' ) ) }
						`,
					}[ this.data.current_plan ] }
				</span>
			</div>
		`;
	}

	/**
	 * Returns the template that displays the addons information
	 *
	 * @return {TemplateResult} The template
	 */
	get _templateSummaryAddons() {
		return html`
			<div class="license-summary-item">
				<uo-icon id="medal"></uo-icon>
				<span class="license-summary-item-value">
					<uo-markdown 
						allowed-tags="strong"
						.content=${ sprintf(
							escapeHTML( _x( '**%1$s out of %2$s addons unlocked** with your current plan', 'License', 'uncanny-automator' ) ),
							this.data.addons?.available,
							this.data.addons?.total
						) }
					></uo-markdown>
				</span>
			</div>
		`;
	}

	/**
	 * Returns the template that displays the call-to-action buttons
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateCTAS() {
		// If there are no CTAs, don't render anything
		if ( isEmpty( this.data.cta ) ) {
			return;
		}

		return html`
			<div class="license-ctas">
				${ repeat(
					this.data.cta,
					( cta, index ) => index,
					cta => this._templateSummaryCTAButton( cta )
				) }
			</div>
		`;
	}

	/**
	 * Returns the template that displays a call-to-action button
	 *
	 * @param {CTAButton} cta - The call-to-action button
	 * @return {TemplateResult} The template
	 */
	_templateSummaryCTAButton( cta ) {
		return html`
			<uo-button
				color="secondary"
				href=${ cta.url }
				target="_blank"
			>
				${ cta.icon ? html`<uo-icon id=${ cta.icon }></uo-icon>` : '' }
				${ cta.label }
			</uo-button>
		`;
	}

	/**
	 * Returns the template that displays an error when there is no data at all
	 *
	 * @return {TemplateResult} The error message
	 */
	get _templateFatalError() {
		return html`
			<uo-alert
				type="error"
				heading=${ escapeHTML( __( 'Something went wrong', 'uncanny-automator' ) ) }
			>
				<p>${ this.fatalErrorMessage }</p>

				<uo-button
					size="small"
					color="secondary"

					@click=${ () => {
						// Remove message
						// This will re render the component, and given that there is no data, it'll do the call again
						this.fatalErrorMessage = '';
						this.data = null;
					} }
				>
					${ escapeHTML( __( 'Try again', 'uncanny-automator' ) ) }
				</uo-button>
			</uo-alert>
		`;
	}
}
